import { useState, useMemo } from 'react';
import {
  View,
  Image,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  StyleSheet,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import * as Auth from 'aws-amplify/auth';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import AppButton from '../components/AppButton';
import alert from '../components/alert';
import { isIOS } from '../util/platform';
import CustomText from '../components/CustomText';
import {
  useFormValidation,
  validateEmail,
  validatePassword,
  validateRequired,
} from '../util/validation/formValidator';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { fonts } from '../style/fonts';
import { useTranslation } from '../i18n';

const ResetPassword = (props) => {
  const { t } = useTranslation('auth');

  const { initialLogin } = props.route.params
    ? props.route.params
    : { initialLogin: false };

  const { isWeb, isDesktop } = useResponsiveStyles();

  const [hidePassword, setHidePassword] = useState(true);

  // Focus states for V3 input styling
  const [emailFocused, setEmailFocused] = useState(false);
  const [codeFocused, setCodeFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Build validation schema based on initialLogin mode
  const validationSchema = useMemo(() => {
    const schema = {
      password: [(v) => validatePassword(v, 8)],
    };

    // Only require email and authCode for non-initial login
    if (!initialLogin) {
      schema.username = [validateEmail];
      schema.authCode = [(v) => validateRequired(v, t('auth:reset.codeFieldName'))];
    }

    return schema;
  }, [initialLogin]);

  // Form validation using centralized hook
  const {
    values,
    isValid: isFormValid,
    setDisplayErrors,
    setFieldValue,
    getFieldError,
  } = useFormValidation(
    { username: '', password: '', authCode: '' },
    validationSchema
  );

  async function isAlreadyAuthenticated() {
    try {
      await Auth.fetchAuthSession();
      return true;
    } catch (e) {
      return false;
    }
  }

  async function handleResetPassword() {
    try {
      setDisplayErrors(true);
      if (!isFormValid) {
        return;
      }

      if (initialLogin) {
        await Auth.confirmSignIn({ challengeResponse: values.password });
      } else {
        await Auth.confirmResetPassword({
          username: values.username,
          confirmationCode: values.authCode,
          newPassword: values.password,
        });
      }

      if (!(await isAlreadyAuthenticated())) {
        await Auth.signIn({
          username: values.username,
          password: values.password,
          options: {
            authFlowType: 'USER_PASSWORD_AUTH',
          },
        });
      }

      Toast.show(`✅ ${t('auth:reset.success')}`, {
        backgroundColor: '#259e23',
        duration: 5000,
        textColor: '#fff',
      });

      props.navigation.navigate('Login', { autoLogin: true });
    } catch (error) {
      alert(t('common:actions.error'), `❌ ${t('resetPassword.changeFailed')}\n${error.toString()}`);
    }
  }

  const icon = hidePassword ? 'eye-off' : 'eye';

  // Responsive widths
  const logoWidth = isDesktop ? 320 : 240;
  const logoHeight = isDesktop ? 89 : 67;
  const cardWidth = isDesktop ? wp(45) : wp(90);
  const linkFontSize = isDesktop ? 18 : 16;

  return (
    <KeyboardAvoidingView
      style={styles.safeAreaContainer}
      behavior={isIOS() ? 'padding' : undefined}
    >
      <TouchableWithoutFeedback
        onPress={!isWeb ? Keyboard.dismiss : () => {}}
        accessible={false}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          keyboardShouldPersistTaps="handled"
        >
          {/* Gold accent bar at top */}
          <View style={styles.accentBar} />

          {/* Logo */}
          <Image
            source={require('../assets/images/logo-black.png')}
            style={[styles.logo, { width: logoWidth, height: logoHeight }]}
          />

          {/* Tagline */}
          <CustomText
            style={styles.tagline}
            font="regular"
            size={16}
            color={AppStyles.colorSet.textSecondary}
          >
            {initialLogin ? t('auth:reset.setNewPassword') : t('auth:reset.resetPassword')}
          </CustomText>

          {/* Form Card */}
          <View style={[styles.formCard, { width: cardWidth }]}>
            {/* Instructions */}
            {!initialLogin && (
              <CustomText
                style={styles.instructions}
                font="regular"
                size={14}
                color={AppStyles.colorSet.textSecondary}
              >
                {t('auth:reset.description')}
              </CustomText>
            )}

            {/* Email input - V3 style (only for non-initial login) */}
            {!initialLogin && (
              <>
                <View
                  style={[
                    styles.inputContainer,
                    emailFocused && styles.inputContainerFocused,
                    { marginTop: hp(2) },
                  ]}
                >
                  <Feather
                    name="mail"
                    size={20}
                    color={emailFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    value={values.username}
                    onChangeText={(text) => setFieldValue('username', text.trim())}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    placeholder={t('auth:reset.emailPlaceholder')}
                    placeholderTextColor={AppStyles.colorSet.textMuted}
                    style={styles.inputText}
                    autoCapitalize="none"
                    autoCorrect={false}
                    keyboardType="email-address"
                    textContentType="emailAddress"
                  />
                </View>
                {getFieldError('username') && (
                  <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                    {getFieldError('username')}
                  </CustomText>
                )}
              </>
            )}

            {/* Verification code input - V3 style (only for non-initial login) */}
            {!initialLogin && (
              <>
                <View
                  style={[
                    styles.inputContainer,
                    codeFocused && styles.inputContainerFocused,
                    { marginTop: hp(2) },
                  ]}
                >
                  <Feather
                    name="hash"
                    size={20}
                    color={codeFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                    style={styles.inputIcon}
                  />
                  <TextInput
                    value={values.authCode}
                    onChangeText={(text) => setFieldValue('authCode', text)}
                    onFocus={() => setCodeFocused(true)}
                    onBlur={() => setCodeFocused(false)}
                    placeholder={t('auth:reset.codePlaceholder')}
                    placeholderTextColor={AppStyles.colorSet.textMuted}
                    style={styles.inputText}
                    keyboardType="numeric"
                  />
                </View>
                {getFieldError('authCode') && (
                  <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                    {getFieldError('authCode')}
                  </CustomText>
                )}
              </>
            )}

            {/* Password input - V3 style */}
            <View
              style={[
                styles.inputContainer,
                passwordFocused && styles.inputContainerFocused,
                { marginTop: hp(2) },
              ]}
            >
              <Feather
                name="lock"
                size={20}
                color={passwordFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                style={styles.inputIcon}
              />
              <TextInput
                value={values.password}
                onChangeText={(text) => setFieldValue('password', text)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                placeholder={t('auth:reset.newPasswordPlaceholder')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                style={styles.inputText}
                autoCapitalize="none"
                autoCorrect={false}
                secureTextEntry={hidePassword}
                textContentType="password"
              />
              <Pressable onPress={() => setHidePassword(!hidePassword)} style={styles.eyeButton}>
                <Feather
                  name={icon}
                  size={20}
                  color={AppStyles.colorSet.textSecondary}
                />
              </Pressable>
            </View>
            <CustomText
              style={styles.helperText}
              font="regular"
              size={14}
              color={AppStyles.colorSet.textMuted}
            >
              {t('auth:reset.minCharacters')}
            </CustomText>
            {getFieldError('password') && (
              <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                {getFieldError('password')}
              </CustomText>
            )}

            {/* Confirm button - V3 style */}
            <View style={{ marginTop: hp(2) }}>
              <AppButton
                title={t('auth:reset.submit')}
                isEnabled={isFormValid}
                onPress={handleResetPassword}
                fullWidth
              />
            </View>
          </View>

          {/* Back to login link */}
          <View style={styles.linksContainer}>
            <Pressable
              onPress={() => props.navigation.navigate('Login')}
              style={[styles.link, { cursor: 'pointer' }]}
            >
              {({ hovered, focused }) => (
                <>
                  <CustomText font="regular" size={linkFontSize} color={AppStyles.colorSet.textSecondary}>
                    {t('auth:reset.backTo')}
                  </CustomText>
                  <CustomText
                    font="semiBold"
                    size={linkFontSize}
                    color={AppStyles.colorSet.brandPrimary}
                    style={(hovered || focused) && { textDecorationLine: 'underline' }}
                  >
                    {t('auth:reset.signIn')}
                  </CustomText>
                </>
              )}
            </Pressable>
          </View>
        </ScrollView>
      </TouchableWithoutFeedback>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  safeAreaContainer: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.whiteColor,
  },
  scrollContainer: {
    alignItems: 'center',
    paddingHorizontal: wp(5),
    paddingTop: hp(2),
    paddingBottom: hp(4),
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  logo: {
    resizeMode: 'contain',
    marginTop: hp(4),
  },
  tagline: {
    marginTop: hp(1),
    marginBottom: hp(3),
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  instructions: {
    textAlign: 'center',
    lineHeight: 22,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputContainerFocused: {
    backgroundColor: AppStyles.colorSet.inputBackgroundFocused,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.inputBorderFocused,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  inputIcon: {
    marginRight: 12,
  },
  inputText: {
    flex: 1,
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  eyeButton: {
    padding: 4,
  },
  helperText: {
    marginTop: 6,
    marginLeft: 4,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 4,
  },
  linksContainer: {
    alignItems: 'center',
    marginTop: hp(3),
  },
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
  },
});

export default ResetPassword;
